/*
 * ============================================================
 *  Free Gift Cart (REMOVE-ONLY)  ·  assets/free-gift-cart.js
 * ------------------------------------------------------------
 *  Adding the gifts is handled elsewhere. This script ONLY
 *  cleans up: if the gift products (from window.freeGiftConfig,
 *  read from the collection metafield custom.gift_products) are
 *  in the cart WITHOUT any trigger ("swish") product, it force-
 *  removes them.
 *
 *  Works on the cart page and the Dawn AJAX cart drawer.
 * ============================================================
 */
(function () {
  'use strict';

  var CONFIG = window.freeGiftConfig || {};
  var KEYWORD = (CONFIG.triggerKeyword || 'swish').toLowerCase();
  var GIFTS = Array.isArray(CONFIG.gifts) ? CONFIG.gifts : [];

  // How long to wait (ms) after removing a surplus gift before refreshing
  // the page, so the drawer shows the change first. Change as needed.
  var REFRESH_DELAY = (typeof CONFIG.refreshDelay === 'number') ? CONFIG.refreshDelay : 2500;

  // sessionStorage flag used to reopen the drawer after the reload.
  var REOPEN_KEY = 'freeGiftReopenDrawer';

  // Grace period (ms) before acting on a detected surplus. Lets a slightly
  // delayed gift/swish add finish so we don't remove a gift during a
  // half-updated cart. Increase if your gift-adder is slow.
  var SETTLE_BUFFER = (typeof CONFIG.settleBuffer === 'number') ? CONFIG.settleBuffer : 1500;

  // Build lookup sets of the gift products/variants to watch for.
  var GIFT_PRODUCT_IDS = {};
  var GIFT_VARIANT_IDS = {};
  GIFTS.forEach(function (g) {
    if (g.productId) GIFT_PRODUCT_IDS[g.productId] = true;
    if (g.variantId) GIFT_VARIANT_IDS[g.variantId] = true;
  });

  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  var internalRequest = false; // true while WE are calling the cart API
  var running = false;
  var pending = false;
  var checkTimer = null;

  /* ----------------------------------------------------------
   * 1. Watch for cart changes (theme, apps, or Dawn drawer)
   *    by intercepting fetch() to the cart endpoints.
   * -------------------------------------------------------- */
  if (originalFetch) {
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        url = args[0] && args[0].url ? args[0].url : String(args[0] || '');
      } catch (e) { url = ''; }

      var isCartMutation = /\/cart\/(add|change|update|clear)(\.js|\.json)?/i.test(url);
      var promise = originalFetch.apply(window, args);

      if (isCartMutation && !internalRequest) {
        promise.then(function () { scheduleCheck(); }).catch(function () {});
      }
      return promise;
    };
  }

  /* Also hook Dawn's pub/sub if present */
  try {
    if (typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleCheck(); });
    }
  } catch (e) { /* older theme without pub/sub */ }

  /* ----------------------------------------------------------
   * 2. Debounced runner (no overlap, idempotent)
   * -------------------------------------------------------- */
  function scheduleCheck() {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(runCheck, 250);
  }

  function runCheck() {
    if (running) { pending = true; return; }
    running = true;
    reconcile()
      .catch(function (err) { console.error('[FreeGift]', err); })
      .then(function () {
        running = false;
        if (pending) { pending = false; scheduleCheck(); }
      });
  }

  /* ----------------------------------------------------------
   * 3. Remove-only logic
   * -------------------------------------------------------- */
  function isGiftItem(item) {
    if (!item) return false;
    return GIFT_PRODUCT_IDS[item.product_id] === true ||
           GIFT_VARIANT_IDS[item.variant_id] === true;
  }

  // Work out how many surplus gift units are in the cart and which lines
  // to trim. Returns { excess, updates }.
  function computeExcess(cart) {
    if (!cart || !Array.isArray(cart.items)) return { excess: 0, updates: {} };

    // How many trigger ("swish") units are in the cart? 1 swish = 1 gift.
    var swishQty = 0;
    cart.items.forEach(function (item) {
      if (!isGiftItem(item) &&
          item.product_title &&
          item.product_title.toLowerCase().indexOf(KEYWORD) !== -1) {
        swishQty += item.quantity;
      }
    });

    // Gift line items in cart order (index 0 = first come).
    var giftLines = cart.items.filter(isGiftItem);
    var totalGiftQty = giftLines.reduce(function (s, l) { return s + l.quantity; }, 0);

    var excess = totalGiftQty - swishQty;
    if (excess <= 0) return { excess: 0, updates: {} };

    // Trim `excess` gift units, first come first removed.
    var updates = {};
    var remaining = excess;
    for (var i = 0; i < giftLines.length && remaining > 0; i++) {
      var line = giftLines[i];
      var reduceBy = Math.min(line.quantity, remaining);
      updates[line.key] = line.quantity - reduceBy; // absolute new qty (0 = removed)
      remaining -= reduceBy;
    }
    return { excess: excess, updates: updates };
  }

  function reconcile() {
    if (!GIFTS.length) return Promise.resolve();

    return getCart().then(function (cart) {
      var info = computeExcess(cart);
      if (info.excess <= 0) return; // within allowance → nothing to do

      // A surplus looks present, but the cart may still be mid-change
      // (e.g. the matching swish add hasn't finished yet). Wait a buffer,
      // then re-read and only remove if the surplus is still real.
      return wait(SETTLE_BUFFER)
        .then(getCart)
        .then(function (freshCart) {
          var confirmed = computeExcess(freshCart);
          if (confirmed.excess <= 0) return; // settled on its own → false alarm
          return applyUpdates(confirmed.updates);
        });
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function applyUpdates(updates) {
    var body = { updates: updates };
    attachSections(body);

    return internalPost('/cart/update.js', body).then(function (cart) {
      // Show the removal in the drawer / cart / header count right away...
      if (cart && cart.sections) renderSections(cart.sections);
      updateDrawerEmptyState(cart);

      // If the drawer is currently open, remember to reopen it after reload.
      if (isDrawerOpen()) {
        try { sessionStorage.setItem(REOPEN_KEY, '1'); } catch (e) {}
      }

      // ...then refresh the whole page after a short delay.
      setTimeout(function () { window.location.reload(); }, REFRESH_DELAY);
    });
  }

  function isDrawerOpen() {
    var d = document.querySelector('cart-drawer');
    return !!(d && d.classList.contains('active'));
  }

  // On load: reopen the drawer if we flagged it, but only if the cart
  // still has items.
  function maybeReopenDrawer() {
    var shouldReopen = false;
    try { shouldReopen = sessionStorage.getItem(REOPEN_KEY) === '1'; } catch (e) {}
    if (!shouldReopen) return;
    try { sessionStorage.removeItem(REOPEN_KEY); } catch (e) {}

    getCart().then(function (cart) {
      if (!cart || !cart.item_count) return; // empty cart → leave it closed
      openCartDrawer();
    }).catch(function () {});
  }

  function openCartDrawer() {
    var doOpen = function () {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer').then(doOpen).catch(doOpen);
    } else {
      doOpen();
    }
  }

  function updateDrawerEmptyState(cart) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !cart || typeof cart.item_count !== 'number') return;
    drawer.classList.toggle('is-empty', cart.item_count === 0);
  }

  /* ----------------------------------------------------------
   * 4. Section rendering (refresh drawer / cart page / bubble)
   * -------------------------------------------------------- */
  function getSectionsToRender() {
    var sections = [];
    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    var mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      sections.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
    }
    var mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) {
      sections.push({ id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' });
    }
    return sections;
  }

  function attachSections(body) {
    var names = getSectionsToRender().map(function (s) { return s.section; });
    var unique = names.filter(function (v, i) { return names.indexOf(v) === i; });
    if (unique.length) {
      body.sections = unique.join(',');
      body.sections_url = window.location.pathname;
    }
  }

  function renderSections(data) {
    getSectionsToRender().forEach(function (s) {
      var html = data[s.section];
      if (!html) return;
      var target = document.getElementById(s.id);
      if (!target) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source = parsed.querySelector(s.selector) || parsed.body;
      var dest = target.querySelector(s.selector) || target;
      if (source) dest.innerHTML = source.innerHTML;
    });
  }

  /* ----------------------------------------------------------
   * 4. Fetch helpers
   * -------------------------------------------------------- */
  function getCart() {
    return fetchJSON('/cart.js', null, 'GET');
  }

  function fetchJSON(url, body, method) {
    var opts = {
      method: method || 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
    };
    if (body) opts.body = JSON.stringify(body);
    var f = originalFetch || window.fetch;
    return f(url, opts).then(function (r) { return r.json(); });
  }

  // Uses originalFetch so our own call never re-triggers the watcher
  function internalPost(url, body) {
    internalRequest = true;
    return fetchJSON(url, body, 'POST').then(
      function (d) { internalRequest = false; return d; },
      function (e) { internalRequest = false; throw e; }
    );
  }

  /* ----------------------------------------------------------
   * 6. Initial sync on load (cleans up stale carts too) +
   *    reopen the drawer if we just refreshed after a removal
   * -------------------------------------------------------- */
  function onReady() {
    scheduleCheck();
    maybeReopenDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
